import React, { useState, useMemo } from 'react';
import HelpTip from './HelpTip';

interface CombinedTableProps {
    data: {
        // Proc data
        totalProcs: number;
        procsByPlayer: Record<
            string,
            {
                name: string;
                class: string;
                count: number;
                totalResource: number;
                spellBreakdown: Record<number, number>;
            }
        >;
        procSpellDetails: Record<number, { name: string; resourceType: string; resourceAmount: number }>;

        // Uptime data
        totalUptime: number;
        uptimeByTarget: Record<
            string,
            {
                name: string;
                class: string;
                uptime: number;
                applicationCount: number;
            }
        >;
        druidInfo: { id: number; name: string; class: string } | null;
        uptimeSpellDetails: Record<number, { name: string; duration: number; rank: string }>;

        // T3 Analysis data
        t3Analysis: {
            t3EquippedFights: number;
            totalT3FightDuration: number;
            procRates: {
                rage: number;
                energy: number;
                mana: number;
            };
            totalResourcesGenerated: {
                rage: number;
                energy: number;
                mana: number;
            };
        };
    };
}

type SortDirection = 'asc' | 'desc' | 'none';
type SortColumn = 'name' | 'class' | 'procCount' | 'totalResource' | 'uptime' | 'applicationCount';

const UptimeTable: React.FC<CombinedTableProps> = ({ data }) => {
    const {
        totalProcs,
        procsByPlayer,
        procSpellDetails,
        totalUptime,
        uptimeByTarget,
        druidInfo,
        uptimeSpellDetails,
        t3Analysis,
    } = data;

    const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>('none');

    // Combine proc and uptime data for each player
    // Start with all players who have uptime data (all players who had Rejuvenation applied)
    const allPlayerKeys = new Set([...Object.keys(procsByPlayer), ...Object.keys(uptimeByTarget)]);

    const combinedData = Array.from(allPlayerKeys).map((playerKey) => {
        const procData = procsByPlayer[playerKey];
        const uptimeData = uptimeByTarget[playerKey];

        return {
            name: procData?.name || uptimeData?.name || 'Unknown',
            class: procData?.class || uptimeData?.class || 'Unknown',
            procCount: procData?.count || 0,
            totalResource: procData?.totalResource || 0,
            uptime: uptimeData?.uptime || 0,
            applicationCount: uptimeData?.applicationCount || 0,
        };
    });

    // Handle column sorting
    const handleSort = (column: SortColumn) => {
        if (sortColumn === column) {
            // Cycle through: asc -> desc -> none
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            } else if (sortDirection === 'desc') {
                setSortDirection('none');
                setSortColumn(null);
            } else {
                setSortDirection('asc');
            }
        } else {
            // New column, start with ascending
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    // Sort the data
    const sortedData = useMemo(() => {
        if (!sortColumn || sortDirection === 'none') {
            return combinedData;
        }

        return [...combinedData].sort((a, b) => {
            let aValue: any = a[sortColumn];
            let bValue: any = b[sortColumn];

            // Handle string comparison for name and class
            if (sortColumn === 'name' || sortColumn === 'class') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (aValue < bValue) {
                return sortDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [combinedData, sortColumn, sortDirection]);

    return (
        <div className="proc-table-container">
            <div className="table-summary">
                <h3>Rejuvenation Analysis</h3>
                <div className="summary-stats">
                    <div className="stat-item">
                        <span className="stat-label">Total Procs:</span>
                        <span className="stat-value">{totalProcs}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Total Uptime:</span>
                        <span className="stat-value">{totalUptime.toFixed(1)}%</span>
                    </div>
                    {druidInfo && (
                        <div className="stat-item">
                            <span className="stat-label">Source Druid:</span>
                            <span className={`stat-value ${druidInfo.class.toLowerCase()}`}>
                                {druidInfo.name} ({druidInfo.class})
                            </span>
                        </div>
                    )}
                    {t3Analysis.t3EquippedFights > 0 && (
                        <>
                            <div className="stat-item">
                                <span className="stat-label">T3 Equipped Fights:</span>
                                <span className="stat-value">{t3Analysis.t3EquippedFights}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label heading-with-help">
                                    Proc rates
                                    <HelpTip label="Proc rates">
                                        <p>Procs per minute of Rejuvenation uptime, split by rage, energy, and mana.</p>
                                    </HelpTip>
                                </span>
                                <span className="stat-value">
                                    Rage: {t3Analysis.procRates.rage.toFixed(2)} | Energy:{' '}
                                    {t3Analysis.procRates.energy.toFixed(2)} | Mana:{' '}
                                    {t3Analysis.procRates.mana.toFixed(2)}
                                </span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Total Resources Generated:</span>
                                <span className="stat-value">
                                    Rage: {t3Analysis.totalResourcesGenerated.rage} | Energy:{' '}
                                    {t3Analysis.totalResourcesGenerated.energy} | Mana:{' '}
                                    {t3Analysis.totalResourcesGenerated.mana}
                                </span>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div className="table-container">
                <table className="proc-table">
                    <thead>
                        <tr>
                            <th
                                className="sortable-header"
                                onClick={() => handleSort('name')}
                                style={{ cursor: 'pointer' }}
                            >
                                Player{' '}
                                {sortColumn === 'name' &&
                                    (sortDirection === 'asc' ? '↑' : sortDirection === 'desc' ? '↓' : '')}
                            </th>
                            <th
                                className="sortable-header"
                                onClick={() => handleSort('class')}
                                style={{ cursor: 'pointer' }}
                            >
                                Class{' '}
                                {sortColumn === 'class' &&
                                    (sortDirection === 'asc' ? '↑' : sortDirection === 'desc' ? '↓' : '')}
                            </th>
                            <th
                                className="sortable-header"
                                onClick={() => handleSort('procCount')}
                                style={{ cursor: 'pointer' }}
                            >
                                Proc Count{' '}
                                {sortColumn === 'procCount' &&
                                    (sortDirection === 'asc' ? '↑' : sortDirection === 'desc' ? '↓' : '')}
                            </th>
                            <th
                                className="sortable-header"
                                onClick={() => handleSort('totalResource')}
                                style={{ cursor: 'pointer' }}
                            >
                                Resources{' '}
                                {sortColumn === 'totalResource' &&
                                    (sortDirection === 'asc' ? '↑' : sortDirection === 'desc' ? '↓' : '')}
                            </th>
                            <th
                                className="sortable-header"
                                onClick={() => handleSort('uptime')}
                                style={{ cursor: 'pointer' }}
                            >
                                Uptime{' '}
                                {sortColumn === 'uptime' &&
                                    (sortDirection === 'asc' ? '↑' : sortDirection === 'desc' ? '↓' : '')}
                            </th>
                            <th
                                className="sortable-header"
                                onClick={() => handleSort('applicationCount')}
                                style={{ cursor: 'pointer' }}
                            >
                                Applications{' '}
                                {sortColumn === 'applicationCount' &&
                                    (sortDirection === 'asc' ? '↑' : sortDirection === 'desc' ? '↓' : '')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedData.map((row, index) => (
                            <tr key={index}>
                                <td className={`player-cell ${row.class.toLowerCase()}`}>{row.name}</td>
                                <td className={`class-cell ${row.class.toLowerCase()}`}>{row.class}</td>
                                <td className="count-cell">{row.procCount}</td>
                                <td className="resource-cell">{row.totalResource}</td>
                                <td className="uptime-cell">{row.uptime.toFixed(1)}%</td>
                                <td className="count-cell">{row.applicationCount}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="spell-details">
                <h4>Spell Information</h4>
                <div className="spell-grid">
                    <h5>Proc Spells</h5>
                    {Object.entries(procSpellDetails).map(([spellId, details]) => (
                        <div key={spellId} className="spell-card">
                            <div className="spell-name">{details.name}</div>
                            <div className="spell-details">
                                <span className="resource-amount">
                                    +{details.resourceAmount} {details.resourceType}
                                </span>
                            </div>
                        </div>
                    ))}

                    <h5>Rejuvenation Spells</h5>
                    {Object.entries(uptimeSpellDetails).map(([spellId, details]) => (
                        <div key={spellId} className="spell-card">
                            <div className="spell-name">{details.name}</div>
                            <div className="spell-details">
                                <span className="duration-amount">
                                    Duration: {details.duration}s ({details.rank})
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default UptimeTable;
